/**
 * AVBP per-tier SERVICE FLAGS (config) — the machine-readable behaviour flags the
 * rest of the system reads off a service tier, per the L1C/L2C integration spec
 * (`L1C_L2C_AVBP_INTEGRATION.md` §1). One source of truth for: billing shape,
 * deliverable kind, whether the intake requires a drawing upload, whether the
 * tier is allowed to capture/contact the architect (the L1C bright line), whether
 * a grid is built, and which tiers an L1C credits toward (the Setu credit).
 *
 * This is config — NOT the money path. The quote/payment resolver
 * (`resolveGateAmount`) stays the single source of the agreed price; these flags
 * drive UI gating + the Setu credit eligibility check, never the amount.
 *
 * Firebase-free + React-free so the `.cjs` test runner exercises it purely.
 */

/** How a tier bills. */
export type BillingShape = 'single-advance-100' | 'milestones';
/** The primary deliverable of a tier. */
export type DeliverableKind = 'report' | 'drawings' | 'drawings+signoff' | 'brief' | 'full-design';

/** The behaviour flags for one service tier. */
export interface ServiceFlags {
  billing: BillingShape;
  deliverable: DeliverableKind;
  /** Does the intake require the client to upload a drawing set? */
  requiresDrawingUpload: boolean;
  /**
   * The L1C/L2C BRIGHT LINE. false → the intake HIDES architect-contact fields and
   * the tier never contacts the architect (L1C). true → architect coordination is
   * part of the engagement (L2C). Machine-enforced (spec §3.3).
   *
   * NOTE (2026-06-25): architect/team CONTACTS moved out of the drawings block into
   * the Design brief's "Coordination · project team" section. This flag now only
   * drives whether the drawings block shows the coordination POINTER note vs the
   * L1C independent-review note, and (via `singleDrawingSet`) single-vs-multi set.
   */
  architectCoordination: boolean;
  /**
   * Does this tier capture exactly ONE drawing set (L1C / L2A — single set), or
   * accumulate revised VERSIONS over time (L2C)? Defaults to single (true) unless
   * the tier is an over-time coordination engagement. Drives the multi-version UI.
   */
  singleDrawingSet?: boolean;
  /** Is a Vastu grid built for this tier (boundary trace → 16-zone/32-entrance)? */
  gridBuild: boolean;
  /** Tiers an L1C payment credits toward within the Setu window (spec §2.3/§6.4). */
  creditsToward?: string[];
}

/**
 * Per-tier flags. Only the tiers with non-default behaviour are listed; the rest
 * fall back to `DEFAULT_FLAGS`. L1C + L2C are the spec-locked entries.
 */
export const SERVICE_FLAGS: Record<string, ServiceFlags> = {
  L1C: {
    billing: 'single-advance-100',
    deliverable: 'report',
    requiresDrawingUpload: true,
    architectCoordination: false, // ← the machine-enforced bright line
    singleDrawingSet: true,
    gridBuild: true,
    creditsToward: ['L2A', 'L2B', 'L2C', 'L2D'],
  },
  // L2A (owner spec 2026-06-25) — drawing-bearing layout tier. Drawings = ONE
  // L1C-style set (single, no over-time versions). The architect/team CONTACTS
  // are captured in the Design brief's Coordination section, so the drawings block
  // shows the coordination POINTER note (architectCoordination:true), NOT the L1C
  // independent-review note. (architectCoordination flags it as a coordination tier
  // for the note; the actual contact capture is in the design brief now.)
  L2A: {
    billing: 'milestones',
    deliverable: 'drawings',
    requiresDrawingUpload: true,
    architectCoordination: true,
    singleDrawingSet: true,
    gridBuild: true,
  },
  L2C: {
    billing: 'milestones',
    deliverable: 'drawings+signoff',
    requiresDrawingUpload: true,
    architectCoordination: true,
    singleDrawingSet: false, // accumulates revised versions over time
    gridBuild: true,
  },
  // L2D · Vistara (detailing) — multi-version drawings (detailing iterates the
  // approved design into working drawings); coordination tier (pointer note).
  L2D: {
    billing: 'milestones',
    deliverable: 'drawings',
    requiresDrawingUpload: true,
    architectCoordination: true,
    singleDrawingSet: false,
    gridBuild: true,
  },
  // L3A · Sankalpa (brief) — drawings OPTIONAL & single (often greenfield).
  L3A: {
    billing: 'milestones',
    deliverable: 'brief',
    requiresDrawingUpload: false,
    architectCoordination: true,
    singleDrawingSet: true,
    gridBuild: true,
  },
  // L3B · Sthapana (design) — multi-version drawings (like L2C); coordination tier.
  L3B: {
    billing: 'milestones',
    deliverable: 'full-design',
    requiresDrawingUpload: true,
    architectCoordination: true,
    singleDrawingSet: false,
    gridBuild: true,
  },
};

const DEFAULT_FLAGS: ServiceFlags = {
  billing: 'milestones',
  deliverable: 'report',
  requiresDrawingUpload: false,
  architectCoordination: false,
  gridBuild: true,
};

/** PURE — strip a trailing `_R{n}` round suffix and upper-case a tier key. */
export function tierKey(tier: string): string {
  return (tier || '').replace(/_R\d+$/i, '').toUpperCase();
}

/** PURE — the flags for a tier (falls back to DEFAULT_FLAGS for unlisted tiers). */
export function flagsForTier(tier: string): ServiceFlags {
  return SERVICE_FLAGS[tierKey(tier)] || DEFAULT_FLAGS;
}

/**
 * PURE — the L1C bright line: should the drawings block HIDE the architect/
 * coordination treatment for this tier (i.e. show the L1C independent-review
 * note instead of the coordination pointer)? True only when the tier is a
 * drawing-bearing tier that explicitly does NOT do coordination (L1C).
 */
export function hidesArchitectContact(tier: string): boolean {
  const f = flagsForTier(tier);
  return f.requiresDrawingUpload && !f.architectCoordination;
}

/**
 * PURE — does this tier capture exactly ONE drawing set (single, no over-time
 * versions)? L1C / L2A = single; L2C = multi (revised versions). A non-drawing
 * tier defaults to single (irrelevant). Explicit `singleDrawingSet` wins; else
 * derive from `architectCoordination` (over-time coordination ⇒ multi).
 */
export function singleDrawingSet(tier: string): boolean {
  const f = flagsForTier(tier);
  if (typeof f.singleDrawingSet === 'boolean') return f.singleDrawingSet;
  return !f.architectCoordination;
}
